import { useModal } from "@ebay/nice-modal-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import ComboBoxMultiple from "components/ComboBoxMultiple";
import ComboBoxMultipleDefaultItem from "components/ComboBoxMultiple/ComboBoxMultipleDefaultItem";
import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import Select from "ds/components/Select";
import SelectItem from "ds/components/Select/Item";
import Textarea from "ds/components/Textarea";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useTypedContext from "hooks/useTypedContext";
import { AI_INTEGRATION_PROVIDER_BEDROCK } from "shared/AiIntegration/constants";
import {
  createAiIntegrationBedrockProviderConfig,
  parseAiIntegrationBedrockProviderConfig,
} from "shared/AiIntegration/providerConfig";
import useAiIntegrationAvailableModels from "shared/AiIntegration/useAvailableAiModels";
import useAvailableAiProviders from "shared/AiIntegration/useAvailableAiProviders";
import useAiIntegrationBedrockInferenceProfiles from "shared/AiIntegration/useBedrockInferenceProfiles";
import useCreateAiIntegration from "shared/AiIntegration/useCreateAiIntegration";
import { AiIntegrationView } from "shared/AiIntegration/useSearchAiIntegrations/gql";
import useUpdateAiIntegration from "shared/AiIntegration/useUpdateAiIntegration";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import { getDrawerFormFix } from "utils/css";
import { stringIsRequired, validateURL } from "utils/formValidators";
import useSearchAwsIntegrations from "views/Account/Integrations/registry/AWS/useSearchCloudIntegrations";
import { SpacesContext } from "views/Account/SpacesProvider";
import { DEFAULT_SPACE_NAME } from "views/constants";

type FormValues = {
  name: string;
  description: string;
  labels: { value: string }[];
  provider: string;
  apiKey: string;
  baseURL: string;
  models: string[];
  space: string;
  providerIntegrationId: string;
  bedrockRegion: string;
  bedrockProfiles: string[];
};

type AiIntegrationFormDrawerProps = {
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  integration?: AiIntegrationView;
  refetchQueriesOnEdit?: string[];
  onCreated?: () => void;
};

const AiIntegrationFormDrawer = createDrawer(
  ({
    integration,
    trackAnalytics,
    refetchQueriesOnEdit,
    onCreated,
  }: AiIntegrationFormDrawerProps) => {
    const drawer = useModal();
    const isEditMode = !!integration?.id;

    const { onError, reportSuccess } = useTypedContext(FlashContext);
    const { manageableSpaces } = useTypedContext(SpacesContext);
    const { integrations: awsIntegrations, isPageLoading: awsIntegrationsLoading } =
      useSearchAwsIntegrations();
    const initialBedrockConfig = parseAiIntegrationBedrockProviderConfig(
      integration?.providerConfig
    );
    const previousSelectedAwsIntegrationIdRef = useRef<string | undefined>(
      integration?.providerIntegrationId ?? undefined
    );
    const lastAutoFilledBedrockRegionRef = useRef("");

    const builderForm = useForm<FormValues>({
      defaultValues: {
        name: integration?.name || "",
        description: integration?.description || "",
        labels: integration?.labels?.map((label) => ({ value: label })) || [],
        space:
          integration?.space?.id ||
          manageableSpaces.find((s) => s.id === DEFAULT_SPACE_NAME)?.id ||
          manageableSpaces[0]?.id,
        provider: integration?.provider,
        apiKey: "",
        baseURL: integration?.baseURL || "",
        models: integration?.models ?? [],
        providerIntegrationId: integration?.providerIntegrationId || "",
        bedrockRegion: initialBedrockConfig?.region || "",
        bedrockProfiles: initialBedrockConfig?.profiles || [],
      },
      mode: "onChange",
      shouldUnregister: true,
    });

    const {
      control,
      getValues,
      register,
      handleSubmit,
      setValue,
      formState: { errors, isDirty },
    } = builderForm;
    const provider = builderForm.watch("provider");
    const providerIntegrationId = builderForm.watch("providerIntegrationId");
    const [committedBedrockRegion, setCommittedBedrockRegion] = useState(
      initialBedrockConfig?.region || ""
    );
    const isBedrockProvider = provider === AI_INTEGRATION_PROVIDER_BEDROCK;

    const { allProviders } = useAvailableAiProviders();

    const providerOptions = useMemo(() => {
      return allProviders.map((pm) => ({
        value: pm.provider,
        label: pm.provider,
      }));
    }, [allProviders]);

    const backendUsableAwsIntegrations = useMemo(() => {
      return awsIntegrations.filter(
        (awsIntegration) => !awsIntegration.generateCredentialsInWorker
      );
    }, [awsIntegrations]);

    const awsIntegrationOptions = useMemo(() => {
      return backendUsableAwsIntegrations.map((awsIntegration) => ({
        value: awsIntegration.id,
        label: awsIntegration.name,
      }));
    }, [backendUsableAwsIntegrations]);

    const selectedAwsIntegration = useMemo(() => {
      return backendUsableAwsIntegrations.find(
        (awsIntegration) => awsIntegration.id === providerIntegrationId
      );
    }, [backendUsableAwsIntegrations, providerIntegrationId]);

    const commitBedrockRegion = useCallback(() => {
      setCommittedBedrockRegion(getValues("bedrockRegion") || "");
    }, [getValues]);

    const liveBedrockProviderConfig = useMemo(() => {
      if (!isBedrockProvider || !selectedAwsIntegration || committedBedrockRegion.trim() === "") {
        return undefined;
      }

      return createAiIntegrationBedrockProviderConfig(committedBedrockRegion);
    }, [committedBedrockRegion, isBedrockProvider, selectedAwsIntegration]);

    const {
      profiles: bedrockInferenceProfiles,
      error: bedrockInferenceProfilesError,
      loading: bedrockInferenceProfilesLoading,
    } = useAiIntegrationBedrockInferenceProfiles({
      awsIntegrationId: selectedAwsIntegration?.id,
      providerConfig: liveBedrockProviderConfig,
    });

    const bedrockInferenceProfileOptions = useMemo(() => {
      return bedrockInferenceProfiles.map((profile) => ({
        value: profile.id,
        label: profile.name,
      }));
    }, [bedrockInferenceProfiles]);

    const bedrockInferenceProfileIDs = useMemo(() => {
      return new Set(bedrockInferenceProfiles.map((profile) => profile.id));
    }, [bedrockInferenceProfiles]);

    const {
      models: availableModels,
      error: modelsError,
      loading: modelsLoading,
      called: modelsCalled,
      loadModels,
    } = useAiIntegrationAvailableModels();

    const pullModels = useCallback(() => {
      loadModels({
        provider: getValues("provider"),
        apiKey: getValues("apiKey"),
        baseURL: getValues("baseURL").trim(),
        space: getValues("space"),
      });
    }, [loadModels, getValues]);

    const availableModelIds = useMemo(
      () => new Set(availableModels.map((model) => model.id)),
      [availableModels]
    );

    const modelOptions = useMemo(() => {
      // Before a pull (editing), show the integration's already-saved models.
      const source = modelsCalled ? availableModels : (integration?.modelDetails ?? []);
      return source.map((model) => ({ value: model.id, label: model.name }));
    }, [modelsCalled, availableModels, integration]);

    const [createIntegration, { loading: creatingIntegration }] = useCreateAiIntegration();

    const [updateIntegration, { loading: updatingIntegration }] = useUpdateAiIntegration({
      refetchQueries: refetchQueriesOnEdit,
    });

    useEffect(() => {
      trackAnalytics("Open Integration Form", {
        type: "ai",
        mode: isEditMode ? "edit" : "create",
      });
    }, [integration, isEditMode, trackAnalytics]);

    useEffect(() => {
      if (!isBedrockProvider) {
        return;
      }

      const currentSelectedAwsIntegrationId = selectedAwsIntegration?.id;
      const previousSelectedAwsIntegrationId = previousSelectedAwsIntegrationIdRef.current;

      if (currentSelectedAwsIntegrationId === previousSelectedAwsIntegrationId) {
        if (
          selectedAwsIntegration?.region &&
          getValues("bedrockRegion") === selectedAwsIntegration.region
        ) {
          lastAutoFilledBedrockRegionRef.current = selectedAwsIntegration.region;
        }

        return;
      }

      const currentRegion = getValues("bedrockRegion");
      const shouldAutoFill =
        currentRegion === "" || currentRegion === lastAutoFilledBedrockRegionRef.current;
      const nextRegion = selectedAwsIntegration?.region || "";

      if (shouldAutoFill && currentRegion !== nextRegion) {
        setValue("bedrockRegion", nextRegion, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setCommittedBedrockRegion(nextRegion);
      }

      if (shouldAutoFill) {
        lastAutoFilledBedrockRegionRef.current = nextRegion;
      }

      previousSelectedAwsIntegrationIdRef.current = currentSelectedAwsIntegrationId;
    }, [getValues, isBedrockProvider, selectedAwsIntegration, setValue]);

    useEffect(() => {
      if (!liveBedrockProviderConfig || bedrockInferenceProfilesLoading) {
        return;
      }

      const currentProfiles = getValues("bedrockProfiles") || [];
      const nextProfiles = currentProfiles.filter((profileId) =>
        bedrockInferenceProfileIDs.has(profileId)
      );

      if (nextProfiles.length === currentProfiles.length) {
        return;
      }

      setValue("bedrockProfiles", nextProfiles, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }, [
      bedrockInferenceProfileIDs,
      bedrockInferenceProfilesLoading,
      getValues,
      liveBedrockProviderConfig,
      setValue,
    ]);

    useEffect(() => {
      if (isBedrockProvider || !modelsCalled || modelsLoading) {
        return;
      }

      const currentModels = getValues("models") || [];
      const nextModels = currentModels.filter((modelId) => availableModelIds.has(modelId));

      if (nextModels.length === currentModels.length) {
        return;
      }

      setValue("models", nextModels, { shouldDirty: true, shouldValidate: false });
    }, [availableModelIds, modelsCalled, isBedrockProvider, modelsLoading, getValues, setValue]);

    const handleCloseDrawer = useCallback(() => {
      drawer.resolve();
      drawer.hide();
    }, [drawer]);

    const onCreateSubmit = useCallback(
      (formData: FormValues) => {
        createIntegration({
          variables: {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            labels: formData.labels.map((label) => label.value),
            space: formData.space,
            provider: formData.provider,
            baseURL: isBedrockProvider ? null : formData.baseURL.trim() || null,
            models: isBedrockProvider ? null : formData.models,
            apiKey: isBedrockProvider ? null : formData.apiKey,
            providerIntegrationId: isBedrockProvider ? formData.providerIntegrationId : null,
            providerConfig: isBedrockProvider
              ? {
                  bedrock: createAiIntegrationBedrockProviderConfig(
                    formData.bedrockRegion,
                    formData.bedrockProfiles
                  ),
                }
              : null,
          },
        })
          .then(({ data }) => {
            if (data?.aiIntegrationCreate?.id) {
              handleCloseDrawer();
              reportSuccess({ message: "AI integration was successfully created" });
              trackAnalytics("Create Integration", { type: "ai" });
              onCreated?.();
            }
          })
          .catch(onError);
      },
      [
        createIntegration,
        handleCloseDrawer,
        isBedrockProvider,
        onCreated,
        onError,
        reportSuccess,
        trackAnalytics,
      ]
    );

    const onUpdateSubmit = useCallback(
      (formData: FormValues) => {
        if (!integration) {
          return;
        }

        updateIntegration({
          variables: {
            id: integration.id,
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            labels: formData.labels.map((label) => label.value),
            space: formData.space,
            provider: formData.provider,
            baseURL: isBedrockProvider ? null : formData.baseURL.trim() || null,
            models: isBedrockProvider || !modelsCalled ? null : formData.models,
            apiKey: isBedrockProvider ? null : formData.apiKey || null,
            providerIntegrationId: isBedrockProvider ? formData.providerIntegrationId : null,
            providerConfig: isBedrockProvider
              ? {
                  bedrock: createAiIntegrationBedrockProviderConfig(
                    formData.bedrockRegion,
                    formData.bedrockProfiles
                  ),
                }
              : null,
          },
        })
          .then(({ data }) => {
            if (data?.aiIntegrationUpdate?.id) {
              reportSuccess({ message: "AI integration was successfully updated" });
              handleCloseDrawer();
              trackAnalytics("Update Integration", { type: "ai" });
            }
          })
          .catch(onError);
      },
      [
        handleCloseDrawer,
        modelsCalled,
        integration,
        isBedrockProvider,
        onError,
        reportSuccess,
        trackAnalytics,
        updateIntegration,
      ]
    );

    const onSubmit = useCallback(
      (formData: FormValues) => {
        if (isEditMode) {
          onUpdateSubmit(formData);
        } else {
          onCreateSubmit(formData);
        }
      },
      [isEditMode, onCreateSubmit, onUpdateSubmit]
    );

    const onClose = useCallback(() => {
      trackAnalytics("Close Integration Form", {
        type: "ai",
        mode: isEditMode ? "edit" : "create",
        dirty: isDirty,
      });
    }, [isDirty, isEditMode, trackAnalytics]);

    const awsIntegrationHelperText = useMemo(() => {
      if (awsIntegrationsLoading) {
        return "Loading AWS integrations";
      }

      if (backendUsableAwsIntegrations.length === 0) {
        return "Create a backend-usable AWS integration first.";
      }

      return undefined;
    }, [awsIntegrationsLoading, backendUsableAwsIntegrations.length]);

    const bedrockProfilesHelperText = useMemo(() => {
      if (bedrockInferenceProfilesLoading) {
        return "Loading Bedrock inference profiles";
      }

      if (bedrockInferenceProfilesError) {
        return "Could not load Bedrock inference profiles for this AWS integration and region.";
      }

      if (bedrockInferenceProfileOptions.length === 0) {
        return "No Bedrock inference profiles available for this AWS integration and region.";
      }

      return undefined;
    }, [
      bedrockInferenceProfileOptions.length,
      bedrockInferenceProfilesError,
      bedrockInferenceProfilesLoading,
    ]);

    const modelsHelperText = useMemo(() => {
      if (!modelsCalled) {
        return "Enter an API key to load available models.";
      }

      if (modelsLoading) {
        return "Loading models from the endpoint.";
      }

      if (modelsError) {
        return "Could not load models. Check the API key and base URL.";
      }

      if (modelOptions.length === 0) {
        return "The endpoint returned no models.";
      }

      return undefined;
    }, [modelsCalled, modelsLoading, modelsError, modelOptions.length]);

    return (
      <DrawerForm isDirty={isDirty} onClose={onClose}>
        <FormProvider {...builderForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle
                title={isEditMode ? "Edit AI integration" : "Set up AI integration"}
              />
              <DrawerCloseIcon />
            </DrawerHeader>

            <DrawerBody gap="x-large">
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Name your integration"
                    error={!!errors?.name}
                    {...register("name", { required: "Name field is required." })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField
                label="Description"
                error={errors?.description?.message}
                isOptional
                noMargin
              >
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Describe your integration"
                    error={!!errors?.description}
                    {...register("description")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldSpace noMargin />

              <Controller
                name="provider"
                control={control}
                rules={{ required: "Provider is required." }}
                render={({ field }) => (
                  <FormField label="Provider" error={errors?.provider?.message} noMargin fullWidth>
                    {({ ariaInputProps }) => (
                      <Select
                        placeholder="Select a provider"
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          setValue("models", []);
                          pullModels();
                        }}
                        items={providerOptions}
                        isDisabled={isEditMode}
                        isError={!!errors?.provider}
                        {...ariaInputProps}
                      >
                        {(item) => <SelectItem {...item} id={item.value} />}
                      </Select>
                    )}
                  </FormField>
                )}
              />

              {isBedrockProvider && (
                <Controller
                  name="providerIntegrationId"
                  control={control}
                  rules={{ required: "AWS integration is required." }}
                  render={({ field }) => (
                    <FormField
                      label="AWS integration"
                      error={errors?.providerIntegrationId?.message}
                      helperText={awsIntegrationHelperText}
                      noMargin
                      fullWidth
                    >
                      {({ ariaInputProps }) => (
                        <Select
                          placeholder="Select an AWS integration"
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          items={awsIntegrationOptions}
                          isLoading={awsIntegrationsLoading}
                          isError={!!errors?.providerIntegrationId}
                          {...ariaInputProps}
                        >
                          {(item) => <SelectItem {...item} id={item.value} />}
                        </Select>
                      )}
                    </FormField>
                  )}
                />
              )}

              {!isBedrockProvider && (
                <FormField
                  label="API Key"
                  error={errors?.apiKey?.message}
                  isOptional={isEditMode}
                  noMargin
                >
                  {({ ariaInputProps }) => (
                    <SecretInput
                      disablePreview
                      placeholder={
                        isEditMode ? "Leave empty to keep existing key" : "Enter your API key"
                      }
                      error={!!errors?.apiKey}
                      {...register("apiKey", {
                        required: isEditMode ? false : "API Key is required.",
                        onBlur: pullModels,
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}

              {!isBedrockProvider && (
                <FormField
                  label="Base URL"
                  error={errors?.baseURL?.message}
                  tooltipInfoVariant="modal"
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>Base URL</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        Override the default provider endpoint for self-hosted gateways (e.g.
                        LiteLLM). Must be https.
                      </TooltipModalBody>
                    </>
                  }
                  isOptional
                  noMargin
                >
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="https://llm.internal.example.com"
                      error={!!errors?.baseURL}
                      {...register("baseURL", {
                        validate: validateURL({
                          https: true,
                          errorMessage: "Enter a valid https:// URL",
                        }),
                        onBlur: pullModels,
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}

              {!isBedrockProvider && !!provider && (
                <Controller
                  name="models"
                  control={control}
                  rules={{
                    // The picker is disabled until the live listing is loaded, and the
                    // submit then leaves models untouched, so only validate a selection
                    // the user could actually make.
                    validate: (value) =>
                      !modelsCalled ||
                      (Array.isArray(value) && value.length > 0) ||
                      "Select at least one model.",
                  }}
                  render={({ field, fieldState }) => (
                    <ComboBoxMultiple<{ value: string; label: string }, string>
                      id="aiIntegrationModels"
                      label="Models"
                      tooltipInfo={
                        <>
                          <TooltipModalTitle>Models</TooltipModalTitle>
                          <TooltipModalBody align="start">
                            The models this integration can use. The list is fetched from the
                            provider with the API key and base URL you enter.
                          </TooltipModalBody>
                        </>
                      }
                      items={modelOptions}
                      values={field.value || []}
                      error={fieldState.error?.message}
                      helperText={modelsHelperText}
                      isLoading={modelsLoading}
                      isDisabled={!modelsCalled || !!modelsError}
                      onChange={(modelId) => {
                        if (!modelId) {
                          return;
                        }

                        const currentValue = field.value || [];
                        if (currentValue.includes(modelId)) {
                          field.onChange(currentValue.filter((value) => value !== modelId));
                          return;
                        }

                        field.onChange([...currentValue, modelId]);
                      }}
                      onBulkValuesChange={field.onChange}
                    >
                      {(item) => (
                        <ComboBoxMultipleDefaultItem checkboxVisible id={item.value} {...item} />
                      )}
                    </ComboBoxMultiple>
                  )}
                />
              )}

              {isBedrockProvider && selectedAwsIntegration && (
                <FormField label="Region" error={errors?.bedrockRegion?.message} noMargin>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Enter AWS region here"
                      error={!!errors?.bedrockRegion}
                      {...register("bedrockRegion", {
                        validate: stringIsRequired("Region is required for Bedrock."),
                        onBlur: commitBedrockRegion,
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}

              {isBedrockProvider &&
                selectedAwsIntegration &&
                committedBedrockRegion.trim() !== "" && (
                  <Controller
                    name="bedrockProfiles"
                    control={control}
                    rules={{
                      validate: (value) =>
                        (Array.isArray(value) && value.length > 0) ||
                        "Select at least one Bedrock inference profile.",
                    }}
                    render={({ field, fieldState }) => (
                      <ComboBoxMultiple<{ value: string; label: string }, string>
                        id="bedrockProfiles"
                        label="Inference profiles"
                        items={bedrockInferenceProfileOptions}
                        values={field.value || []}
                        error={fieldState.error?.message}
                        helperText={bedrockProfilesHelperText}
                        isLoading={bedrockInferenceProfilesLoading}
                        isDisabled={bedrockInferenceProfilesLoading}
                        onChange={(profileId) => {
                          if (!profileId) {
                            return;
                          }

                          const currentValue = field.value || [];
                          if (currentValue.includes(profileId)) {
                            field.onChange(currentValue.filter((value) => value !== profileId));
                            return;
                          }

                          field.onChange([...currentValue, profileId]);
                        }}
                        onBulkValuesChange={field.onChange}
                      >
                        {(item) => (
                          <ComboBoxMultipleDefaultItem checkboxVisible id={item.value} {...item} />
                        )}
                      </ComboBoxMultiple>
                    )}
                  />
                )}

              <Box direction="column">
                <FormFieldTags label="Labels" tagName="label" name="labels" isOptional noMargin />
              </Box>

              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={creatingIntegration || updatingIntegration}
                    loading={creatingIntegration || updatingIntegration}
                  >
                    {isEditMode ? "Save" : "Set up"}
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showAiIntegrationFormDrawer = createDrawerTrigger(AiIntegrationFormDrawer);
